import { Platform } from '@angular/cdk/platform';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AuthService } from '../auth';
import { CookieService } from '../cookie';
import { AbstractLogger, LoggerFactory } from '../diagnostics';
import { AppVersionService, CheckVersionGuard } from './abstract';

@Injectable()
export class CheckVersionGuardImpl implements CheckVersionGuard {
	private _logger: AbstractLogger;

	constructor(
		protected readonly platform: Platform,
		protected readonly cookies: CookieService,
		protected readonly auth: AuthService,
		protected readonly version: AppVersionService,
		logger: LoggerFactory
	) {
		this._logger = logger.createLogger('CheckVersionGuard');
	}

	canActivate(
		next: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<boolean> | Promise<boolean> | boolean {
		this._logger.forceLogInfo('check actual version');
		return this.version.getAppInfoFromAssetFile().pipe(
			map(appInfo => {
				const runtimeVersion = this.getVersionNumber(this.version.appInfo.version);
				const deployVersion = this.getVersionNumber(appInfo.version);
				let needInvalidateCache = runtimeVersion !== deployVersion;
				if (needInvalidateCache) {
					this._logger.forceLogInfo(
						`runtimeVersion: ${runtimeVersion}, deployVersion: ${deployVersion}, needInvalidateCache: ${needInvalidateCache}`
					);
					return needInvalidateCache;
				}
				const buildNumber = this.version.getRuntimeBuildNumber();
				needInvalidateCache = this.version.hasRuntimeBuildNumber()
					? buildNumber !== Number(appInfo.build)
					: needInvalidateCache;

				if (needInvalidateCache) {
					this._logger.forceLogInfo(
						`runtimeBuild: ${buildNumber}, deployVersion: ${appInfo.build}, needInvalidateCache: ${needInvalidateCache}`
					);
					return needInvalidateCache;
				}

				return needInvalidateCache;
			}),
			tap(needInvalidateCache => {
				if (needInvalidateCache) {
					// hard reload for update version
					location.reload();
				}
			}),
			map(needInvalidateCache => !needInvalidateCache)
		);
	}

	/**
	 * 3.189.0 => 31890
	 * @param version value like 3.189.0
	 */
	protected getVersionNumber(version: string): number {
		return Number((version || '0').split('.').join(''));
	}
}
